import { useState, useEffect } from "react";
import WorkspaceService from "services/workspace-service";

/**
 * Hook to manage AI usage limits (read, sync, event listen).
 * @param {string} storageKey - Key for localStorage caching.
 * @returns {Object} { usage, updateUsage }
 */
export const useAiUsage = () => {
    const workspaceId = localStorage.getItem("activeWorkspaceId") || "default";
    const storageKey = `ai_usage_ws_${workspaceId}`;
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        // 1. Try Local Storage first for speed
        const cached = localStorage.getItem(storageKey);
        if (cached) {
            try {
                setUsage(JSON.parse(cached));
            } catch (e) {
                console.error("Failed to parse cached usage", e);
            }
        }

        // 2. Fetch Workspace Entitlements for Truth (Lazy sync)
        WorkspaceService.fetchWorkspaceEntitlements().then(entitlements => {
            if (entitlements && entitlements.usage && entitlements.limits) {
                const current = entitlements.usage.ai_prompt_limit_daily || 0;
                const limit = entitlements.limits.ai_prompt_limit_daily || 0;

                // Construct standard usage object
                const serverUsage = {
                    current: current,
                    limit: limit,
                    remaining: Math.max(0, limit - current)
                };

                updateUsage(serverUsage);
            }
        }).catch(err => console.error("Failed to sync AI usage:", err));

        // 3. Listen for Global Usage Updates (Sync between charts)
        const handleUsageUpdate = (event) => {
            if (event.detail) {
                setUsage(event.detail);
                // No need to setItem here as the dispatcher did it
            }
        };

        window.addEventListener("comparedly.aiUsageUpdated", handleUsageUpdate);

        return () => {
            window.removeEventListener("comparedly.aiUsageUpdated", handleUsageUpdate);
        };
    }, []);

    const updateUsage = (newUsage) => {
        setUsage(newUsage);
        localStorage.setItem(storageKey, JSON.stringify(newUsage));

        // Dispatch event for other charts
        window.dispatchEvent(
            new CustomEvent("comparedly.aiUsageUpdated", { detail: newUsage })
        );
    };

    return { usage, updateUsage };
};
